import React, { Suspense, createContext, useContext, useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useMessageInstances } from '@atlassian/post-office-headless-api/hooks/useMessageInstances';

import { type ThirdPartyConfigsBootstrap } from '../../../constants/schemas/3p-config';
import {
	mapToAvailableConnectors,
	orderConfigByProductOrder,
	type useOAuthHandlers,
} from '../banner-placement';

import {
	QUICK_FIND_TEMPLATE_ID,
	type QuickFindHeadlessAvailableConnectors,
	type QuickFindHeadlessMessage,
	type QuickFindHeadlessMessageInstance,
	type QuickFindHeadlessRendererProps,
} from './headless-types';

const loadQuickFindHeadlessRenderer = () =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/search-page/quick-find-headless-message-renderer" */ './headless-renderer'
	);

const QuickFindHeadlessMessageRenderer = Object.assign(
	React.lazy<React.ComponentType<QuickFindHeadlessRendererProps>>(() =>
		loadQuickFindHeadlessRenderer().then((module) => ({
			default: module.QuickFindHeadlessMessageRenderer,
		})),
	),
	{ displayName: 'lazy(QuickFindHeadlessMessageRenderer)' },
);

interface QuickFindHeadlessPlacementProps {
	placementId: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProductsOrder: string[]; // Product keys in display order; connectors will be shown in this order
	useOAuthHandlers: useOAuthHandlers;
}

type QuickFindHeadlessPlacementData = {
	messages: QuickFindHeadlessMessageInstance[];
	availableConnectors: QuickFindHeadlessAvailableConnectors;
	useOAuthHandlers: useOAuthHandlers;
	dismissMessage: (messageInstanceId: string) => void;
	PostOfficeMessageInstance: ReturnType<
		typeof useMessageInstances<QuickFindHeadlessMessage>
	>['PostOfficeMessageInstance'];
};

const QuickFindHeadlessPlacementContext = createContext<QuickFindHeadlessPlacementData | null>(
	null,
);

export const QuickFindHeadlessPlacementDataProvider = ({
	placementId,
	thirdPartyConfigs,
	availableProductsOrder,
	useOAuthHandlers,
	children,
}: React.PropsWithChildren<QuickFindHeadlessPlacementProps>): React.JSX.Element => {
	const { messages, dismissMessage, PostOfficeMessageInstance } =
		useMessageInstances<QuickFindHeadlessMessage>(
			{
				placementId,
				messageTemplateId: QUICK_FIND_TEMPLATE_ID,
			},
			{
				dataOutsideWrapper: true,
			},
		);

	useEffect(() => {
		if (!fg('post-office_enable_search-quick-find-headless')) {
			return;
		}

		void loadQuickFindHeadlessRenderer();
	}, []);

	const orderedConfigs = useMemo(
		() => orderConfigByProductOrder(thirdPartyConfigs, availableProductsOrder),
		[thirdPartyConfigs, availableProductsOrder],
	);

	const availableConnectors = useMemo(
		() => mapToAvailableConnectors(orderedConfigs),
		[orderedConfigs],
	);

	const value = useMemo(
		() => ({
			messages,
			availableConnectors,
			useOAuthHandlers,
			dismissMessage,
			PostOfficeMessageInstance,
		}),
		[messages, availableConnectors, useOAuthHandlers, dismissMessage, PostOfficeMessageInstance],
	);

	return (
		<QuickFindHeadlessPlacementContext.Provider value={value}>
			{children}
		</QuickFindHeadlessPlacementContext.Provider>
	);
};

export const QuickFindHeadlessVisiblePlacement = (): React.JSX.Element | null => {
	const placement = useContext(QuickFindHeadlessPlacementContext);

	if (!placement) {
		return null;
	}

	const {
		messages,
		availableConnectors,
		useOAuthHandlers,
		dismissMessage,
		PostOfficeMessageInstance,
	} = placement;

	return (
		<Suspense fallback={null}>
			{messages.map((message) => (
				<QuickFindHeadlessMessageRenderer
					key={message.messageInstanceId}
					message={message}
					availableConnectors={availableConnectors}
					useOAuthHandlers={useOAuthHandlers}
					dismissMessage={dismissMessage}
					PostOfficeMessageInstance={PostOfficeMessageInstance}
				/>
			))}
		</Suspense>
	);
};
